import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { tap, catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ProjectPublic, ProjectDetail } from '../models';
import { ImageFallbackService } from './image-fallback.service';
import { ProjectsResponse } from '../models/project_response';
import { ProjectType } from '../enums/project-type.enum';
import { SearchFilters } from '../search/search.component';
import { SearchResponse } from '../models/project_response';

export interface LikeResponse {
  status: boolean;
  liked: boolean;
  likes_count: number;
  liked_at?: string;
  cooldown_seconds?: number;
  cooldown_remaining?: number;
  message?: string;
}

interface CachedData<T> {
  data: T;
  timestamp: number;
}

@Injectable({
  providedIn: 'root',
})
export class ProjectService {
  private apiUrl = environment.apiUrl;
  private readonly CACHE_DURATION = 5 * 60 * 1000;

  constructor(
    private http: HttpClient,
    private imageFallback: ImageFallbackService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  private publicProjectsCache: CachedData<ProjectPublic[]> | null = null;
  private userProjectsCache: CachedData<any[]> | null = null;
  private projectDetailsCache = new Map<string, CachedData<ProjectDetail>>();

  private navigationState: boolean = false;
  private homeState: any = null;

  private worldCupRoundsCache = new Map<number, number[]>();

  setAvailableRounds(idform: number, rounds: number[]): void {
    this.worldCupRoundsCache.set(idform, rounds);
  }

  getCachedRounds(idform: number): number[] {
    return this.worldCupRoundsCache.get(idform) ?? [];
  }

  private getCacheKey(id: number, includes: string[]): string {
    if (includes.length === 0) return `${id}`;
    return `${id}:${includes.sort().join(',')}`;
  }

  private isCacheValid<T>(cache: CachedData<T> | null): boolean {
    if (!cache) return false;
    return Date.now() - cache.timestamp < this.CACHE_DURATION;
  }

  clearAllCache(): void {
    this.publicProjectsCache = null;
    this.userProjectsCache = null;
    this.projectDetailsCache.clear();
  }

  invalidatePublicProjectsCache(): void {
    this.publicProjectsCache = null;
  }

  invalidateUserProjectsCache(): void {
    this.userProjectsCache = null;
  }

  invalidateProjectCache(id: number): void {
    const prefix = `${id}`;
    for (const key of this.projectDetailsCache.keys()) {
      if (key === prefix || key.startsWith(`${prefix}:`)) {
        this.projectDetailsCache.delete(key);
      }
    }
  }

  private normalizeProjectDetail(raw: any): ProjectDetail {
    const data = raw?.data ?? raw;

    const tipoform: number =
      typeof data.tipoform === 'object' && data.tipoform !== null
        ? Number(data.tipoform.value)
        : Number(data.tipoform);

    const normalized = { ...data, tipoform } as ProjectDetail;

    if (!normalized.user) {
      console.warn(
        '⚠️ ProjectService - normalizeProjectDetail: Proyecto sin usuario',
        {
          projectId: normalized.idform,
        },
      );
    }

    // Mapear imágenes
    return this.mapProjectImages(normalized) as ProjectDetail;
  }

  /**
   * Centralized mapping for project and user images
   */
  private mapProjectImages<T extends ProjectPublic | ProjectDetail>(p: T): T {
    if (!p) return p;

    // Miniatura del proyecto
    if (p.miniatura_url !== undefined) {
      p.optimizedThumbnail = this.imageFallback.getConstructedUrl(
        p.miniatura_url,
        'project',
      );
    }

    // Imagen de perfil del usuario
    if (p.user) {
      p.user.optimizedProfile = this.imageFallback.getConstructedUrl(
        p.user.profile_picture_url,
        'profile',
      );
    }

    return p;
  }

  // ==================== PROYECTOS PÚBLICOS ====================

  getProjects(
    page: number = 1,
    perPage: number = 10,
    searchTerm?: string,
    filters?: SearchFilters,
  ): Observable<ProjectsResponse | any> {
    if (searchTerm?.trim()) {
      let params = new HttpParams()
        .set('q', searchTerm)
        .set('page', page.toString())
        .set('perPage', perPage.toString());

      if (filters) {
        if (filters.type && filters.type !== 'all')
          params = params.set('type', filters.type);
        if (filters.sortBy) params = params.set('sort_by', filters.sortBy);
        if (filters.sortOrder) params = params.set('order', filters.sortOrder);
      }

      return this.http
        .get<
          ProjectsResponse | any
        >(`${this.apiUrl}/searchProjects`, { params })
        .pipe(catchError((error) => throwError(() => error)));
    }

    if (page === 1 && this.isCacheValid(this.publicProjectsCache)) {
      const cachedProjects = this.publicProjectsCache!.data;
      const total = cachedProjects.length;
      return of({
        status: true,
        projects: cachedProjects,
        users: [],
        total,
        current_page: page,
        per_page: perPage,
        last_page: Math.ceil(total / perPage),
      });
    }

    const params = new HttpParams()
      .set('page', page.toString())
      .set('perPage', perPage.toString());

    return this.http
      .get<ProjectsResponse>(`${this.apiUrl}/getPublicProjects`, { params })
      .pipe(
        map((response) => {
          if (response.status && response.projects) {
            response.projects = response.projects.map((p) =>
              this.mapProjectImages(p),
            );
          }
          return response;
        }),
        tap((response) => {
          if (response.status && page === 1) {
            this.publicProjectsCache = {
              data: response.projects,
              timestamp: Date.now(),
            };
          }
        }),
        catchError((error) => {
          if (page === 1 && this.publicProjectsCache) {
            return of({
              status: true,
              projects: this.publicProjectsCache.data,
              users: [],
              total: this.publicProjectsCache.data.length,
              current_page: page,
              per_page: perPage,
              last_page: Math.ceil(
                this.publicProjectsCache.data.length / perPage,
              ),
              fromCache: true,
            } as ProjectsResponse);
          }
          return throwError(() => error);
        }),
      );
  }

  getProjectFull(idform: number): Observable<ProjectDetail> {
    return this.http
      .get<any>(
        `${this.apiUrl}/projects/${idform}/full?include=user,description`,
      )
      .pipe(
        map((response) => this.normalizeProjectDetail(response)),
        tap((project) => {
          // Podríamos guardar en cache también si es necesario,
          // pero por ahora solo normalizamos la data para que sea compatible.
        }),
      );
  }

  getPublicProjectsByUser(
    userId: number,
    perPage: number = 50,
  ): Observable<any> {
    return this.http.get<any>(
      `${this.apiUrl}/getPublicProjectsByUser/${userId}`,
      {
        params: { perPage },
      },
    );
  }

  // ==================== PROYECTOS DEL USUARIO ====================

  getUserProjects(
    page: number = 1,
    perPage: number = 10,
    searchTerm?: string,
    filters?: { sortBy?: string; sortOrder?: string },
    forceRefresh: boolean = false,
  ): Observable<any> {
    const hasSearch = !!(
      searchTerm?.trim() ||
      (filters &&
        (filters.sortBy !== 'created_at' || filters.sortOrder !== 'desc'))
    );

    if (
      !forceRefresh &&
      !hasSearch &&
      page === 1 &&
      this.isCacheValid(this.userProjectsCache)
    ) {
      return of({
        status: true,
        projects: this.userProjectsCache!.data,
        total: this.userProjectsCache!.data.length,
        current_page: 1,
        per_page: perPage,
        last_page: Math.ceil(this.userProjectsCache!.data.length / perPage),
        fromCache: true,
      });
    }

    let params = new HttpParams()
      .set('page', page.toString())
      .set('perPage', perPage.toString());

    if (searchTerm?.trim()) {
      params = params.set('q', searchTerm);
    }

    if (filters) {
      if (filters.sortBy) params = params.set('sort_by', filters.sortBy);
      if (filters.sortOrder) params = params.set('order', filters.sortOrder);
    }

    return this.http
      .get<any>(`${this.apiUrl}/getUserProjects`, { params })
      .pipe(
        map((response) => {
          if (response.status && response.projects) {
            response.projects = response.projects.map((p: any) =>
              this.mapProjectImages(p),
            );
          }
          return response;
        }),
        tap((response) => {
          if (response.status && !hasSearch && page === 1) {
            this.userProjectsCache = {
              data: response.projects,
              timestamp: Date.now(),
            };
          }
        }),
        catchError((error) => {
          if (this.userProjectsCache && page === 1 && !hasSearch) {
            return of({
              status: true,
              projects: this.userProjectsCache.data,
              fromCache: true,
              apiError: true,
            });
          }
          return throwError(() => error);
        }),
      );
  }

  // ==================== DETALLES DE PROYECTO ====================

  getProjectDetail(
    id: number,
    options?: {
      includeItems?: boolean;
      includeDescription?: boolean;
      includeUser?: boolean;
      forceRefresh?: boolean;
    },
  ): Observable<ProjectDetail> {
    const includes: string[] = [];
    if (options?.includeItems) includes.push('items');
    if (options?.includeDescription) includes.push('description');
    if (options?.includeUser) includes.push('user');

    const cacheKey = this.getCacheKey(id, includes);
    const cached = this.projectDetailsCache.get(cacheKey);

    // Un solo bloque de caché, forceRefresh lo omite completamente
    if (!options?.forceRefresh && cached && this.isCacheValid(cached)) {
      return of(cached.data);
    }

    let params = new HttpParams();
    if (includes.length > 0) params = params.set('include', includes.join(','));

    return this.http
      .get<unknown>(`${this.apiUrl}/projects/${id}`, { params })
      .pipe(
        map((response) => this.normalizeProjectDetail(response)),
        tap((project) => {
          this.projectDetailsCache.set(cacheKey, {
            data: project,
            timestamp: Date.now(),
          });
        }),
        catchError((error) => {
          if (cached) return of(cached.data); // fallback al caché viejo si hay error de red
          return throwError(() => error);
        }),
      );
  }

  // ==================== CREACIÓN Y ACTUALIZACIÓN ====================

  createProject(data: {
    titulo: string;
    descripcion: string;
    tipo: ProjectType;
    randoms?: boolean;
    miniatura_url?: string;
  }): Observable<any> {
    // Mapear nombres de campos al formato esperado por el backend.
    // 'high_quality_thumbnail' NO se envía aquí: es función PRO y solo se
    // habilita vía PUT /projects/{idform}/upgrade-miniatura (middleware 'pro').
    const payload = {
      nameform: data.titulo,
      descripcion: data.descripcion,
      tipoform: data.tipo,
      randoms: data.randoms,
      miniatura: data.miniatura_url,
    };

    return this.http
      .post(`${this.apiUrl}/createProjectTable`, payload)
      .pipe(tap(() => this.invalidateUserProjectsCache()));
  }

  updateProject(idform: number, updatedProject: any): Observable<any> {
    return this.http
      .put(`${this.apiUrl}/projects/${idform}`, updatedProject)
      .pipe(
        tap(() => {
          this.invalidateProjectCache(idform);
          this.invalidateUserProjectsCache();
          this.invalidatePublicProjectsCache();
        }),
      );
  }

  // ==================== ELIMINACIÓN ====================

  deleteProject(idform: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/projects/${idform}`).pipe(
      tap(() => {
        this.invalidateProjectCache(idform);
        this.invalidateUserProjectsCache();
        this.invalidatePublicProjectsCache();
      }),
    );
  }

  // ==================== MINIATURAS ====================

  uploadMiniatura(idform: number, file: File): Observable<any> {
    if (!file.type.startsWith('image/')) {
      return throwError(() => new Error('El archivo debe ser una imagen'));
    }
    if (file.size > 10 * 1024 * 1024) {
      return throwError(() => new Error('La imagen no debe superar 10MB'));
    }

    const formData = new FormData();
    formData.append('image', file);

    return this.http
      .post(`${this.apiUrl}/projects/${idform}/upload-miniatura`, formData)
      .pipe(
        tap(() => {
          this.invalidateProjectCache(idform);
          this.invalidateUserProjectsCache();
          this.invalidatePublicProjectsCache();
        }),
        catchError((error) => throwError(() => error)),
      );
  }

  updateMiniaturaUrl(idform: number, url: string): Observable<any> {
    if (!url || !url.startsWith('http')) {
      return throwError(
        () => new Error('La URL debe comenzar con http:// o https://'),
      );
    }

    return this.http
      .put(`${this.apiUrl}/projects/${idform}/update-miniatura-url`, { url })
      .pipe(
        tap(() => {
          this.invalidateProjectCache(idform);
          this.invalidateUserProjectsCache();
          this.invalidatePublicProjectsCache();
        }),
        catchError((error) => throwError(() => error)),
      );
  }

  upgradeMiniatura(idform: number): Observable<any> {
    return this.http
      .put(`${this.apiUrl}/projects/${idform}/upgrade-miniatura`, {})
      .pipe(
        tap(() => {
          this.invalidateProjectCache(idform);
          this.invalidateUserProjectsCache();
          this.invalidatePublicProjectsCache();
        }),
        catchError((error) => throwError(() => error)),
      );
  }

  // ==================== LIKES ====================

  /**
   * Send a like for a project.
   */
  likeProject(idform: number): Observable<LikeResponse> {
    return this.http
      .post<LikeResponse>(`${this.apiUrl}/projects/${idform}/like`, {})
      .pipe(catchError((error) => throwError(() => error)));
  }

  /**
   * Remove a like from a project.
   * The server returns 423 if the cooldown has not expired yet.
   */
  unlikeProject(idform: number): Observable<LikeResponse> {
    return this.http
      .delete<LikeResponse>(`${this.apiUrl}/projects/${idform}/like`)
      .pipe(catchError((error) => throwError(() => error)));
  }

  /**
   * Check if the authenticated user has liked a project.
   */
  hasLikedProject(idform: number): Observable<LikeResponse> {
    return this.http
      .get<LikeResponse>(`${this.apiUrl}/projects/${idform}/liked`)
      .pipe(catchError((error) => throwError(() => error)));
  }

  // ==================== ESTADO Y NAVEGACIÓN ====================

  getTierlistSave(idform: number): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/projects/${idform}/tierlist`);
  }

  saveTierlist(idform: number, payload: any): Observable<any> {
    return this.http.put(`${this.apiUrl}/projects/${idform}/tierlist`, payload);
  }

  setNavigationState(state: boolean): void {
    this.navigationState = state;
  }
  getNavigationState(): boolean {
    return this.navigationState;
  }
  resetNavigationState(): void {
    this.navigationState = false;
  }

  setHomeState(state: any): void {
    this.homeState = state;
  }
  getHomeState(): any {
    return this.homeState;
  }
  clearHomeState(): void {
    this.homeState = null;
  }
}
